// auto-layout for strain graphs.
// A compact Sugiyama-style layout with two idea-map-specific adjustments:
//   1. edges read in the argument direction: foundations -> inferences -> thesis
//   2. crowded layers are packed into measured subcolumns instead of one tall stack
//
// Node heights are estimated from text length so first-visit graphs do not
// overlap and do not zoom out just because five claims landed in one column.

import type { GraphEdge, GraphNode, ID, RelationType, StrainGraph } from "@/types";
import { edgeMeta } from "@/lib/vocabulary";
import {
  chooseLabelPoint, estimateEdgeLabelSize, expandedRect, rectOverlapArea, type Rect,
} from "@/lib/edge-geometry";

const NODE_W = 280;
const COL_W = 450;
const PAD_X = 64;
const PAD_Y = 52;
const CENTER_Y = 320;
const ROW_GAP = 74;
const MAX_STACKED_NODES = 4;
const LABEL_GAP = 20;
const CRITIQUE_X_GAP = 340;
const CRITIQUE_Y_GAP = 44;

function pushUnique(map: Map<ID, ID[]>, key: ID, value: ID) {
  const list = map.get(key);
  if (list && !list.includes(value)) list.push(value);
}

function estimateNodeHeight(node: GraphNode): number {
  const isThesis = node.badges?.includes("thesis");
  const charsPerLine = isThesis ? 39 : 43;
  const textLines = Math.max(2, Math.ceil(node.text.length / charsPerLine));
  const visibleLines = Math.min(textLines, 8);
  // stats only render on focus now; the attachment tray is the default extra row
  const trayRow = node.variants?.length || node.attachment ? 28 : 0;
  const statusSlack = node.status === "needs_evidence" || node.provenance === "ai_inferred" ? 8 : 0;
  return Math.min(230, Math.max(isThesis ? 100 : 88, 48 + visibleLines * 17 + trayRow + statusSlack));
}

function estimateLabelSize(edge: GraphEdge): { w: number; h: number } {
  const label = edge.label ?? edgeMeta(edge.relation).verb;
  return estimateEdgeLabelSize(label, edge.stats?.undercuts ?? 0);
}

function rectForNode(node: GraphNode, position: { x: number; y: number }): Rect {
  const h = estimateNodeHeight(node);
  return {
    left: position.x,
    right: position.x + NODE_W,
    top: position.y,
    bottom: position.y + h,
  };
}

function closestPointOnRect(rect: Rect, tx: number, ty: number): { x: number; y: number } {
  const cx = (rect.left + rect.right) / 2;
  const cy = (rect.top + rect.bottom) / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { x: dx >= 0 ? rect.right : rect.left, y: cy };
  }
  return { x: cx, y: dy >= 0 ? rect.bottom : rect.top };
}

function edgeLabelRect(
  edge: GraphEdge,
  edgeById: Map<ID, GraphEdge>,
  nodeById: Map<ID, GraphNode>,
  positions: Record<ID, { x: number; y: number }>,
): Rect | null {
  const source = nodeById.get(edge.source);
  const sourcePos = positions[edge.source];
  if (!source || !sourcePos) return null;
  const sourceRect = rectForNode(source, sourcePos);
  const sourceCenter = {
    x: (sourceRect.left + sourceRect.right) / 2,
    y: (sourceRect.top + sourceRect.bottom) / 2,
  };

  let tx: number;
  let ty: number;
  if (edge.relation === "undercuts" && edge.target_edge) {
    const targetEdge = edgeById.get(edge.target_edge);
    const targetSource = targetEdge ? nodeById.get(targetEdge.source) : null;
    const targetTarget = targetEdge?.target ? nodeById.get(targetEdge.target) : null;
    const targetSourcePos = targetEdge ? positions[targetEdge.source] : null;
    const targetTargetPos = targetEdge?.target ? positions[targetEdge.target] : null;
    if (!targetSource || !targetTarget || !targetSourcePos || !targetTargetPos) return null;
    const sr = rectForNode(targetSource, targetSourcePos);
    const tr = rectForNode(targetTarget, targetTargetPos);
    tx = (sr.right + tr.left) / 2;
    ty = ((sr.top + sr.bottom) / 2 + (tr.top + tr.bottom) / 2) / 2;
  } else if (edge.target) {
    const target = nodeById.get(edge.target);
    const targetPos = positions[edge.target];
    if (!target || !targetPos) return null;
    const targetRect = rectForNode(target, targetPos);
    tx = (targetRect.left + targetRect.right) / 2;
    ty = (targetRect.top + targetRect.bottom) / 2;
  } else {
    return null;
  }

  const sourcePoint = closestPointOnRect(sourceRect, tx, ty);
  const targetPoint = { x: tx, y: ty };
  if (edge.target && edge.relation !== "undercuts") {
    const target = nodeById.get(edge.target);
    const targetPos = positions[edge.target];
    if (target && targetPos) {
      const targetRect = rectForNode(target, targetPos);
      const p = closestPointOnRect(targetRect, sourceCenter.x, sourceCenter.y);
      targetPoint.x = p.x;
      targetPoint.y = p.y;
    }
  }

  const label = estimateLabelSize(edge);
  const nodeRects = Object.entries(positions).flatMap(([id, position]) => {
    const node = nodeById.get(id);
    return node ? [rectForNode(node, position)] : [];
  });
  const labelPoint = chooseLabelPoint({
    baseX: (sourcePoint.x + targetPoint.x) / 2,
    baseY: (sourcePoint.y + targetPoint.y) / 2,
    sx: sourcePoint.x,
    sy: sourcePoint.y,
    tx: targetPoint.x,
    ty: targetPoint.y,
    labelW: label.w,
    labelH: label.h,
    nodeRects,
    clearance: LABEL_GAP,
  });
  return labelPoint.rect;
}

function relaxForEdgeLabels(
  graph: StrainGraph,
  positions: Record<ID, { x: number; y: number }>,
  nodeById: Map<ID, GraphNode>,
): void {
  const edgeById = new Map(graph.edges.map((edge) => [edge.id, edge]));
  for (let pass = 0; pass < 8; pass += 1) {
    let moved = false;
    for (const edge of graph.edges) {
      const labelRect = edgeLabelRect(edge, edgeById, nodeById, positions);
      if (!labelRect) continue;

      for (const node of graph.nodes) {
        const position = positions[node.id];
        if (!position) continue;
        const nodeRect = expandedRect(rectForNode(node, position), LABEL_GAP);
        if (rectOverlapArea(labelRect, nodeRect) === 0) continue;

        const isThesis = node.badges?.includes("thesis");
        const fallbackId = edge.source !== node.id ? edge.source : edge.target;
        const moveId = isThesis && fallbackId ? fallbackId : node.id;
        const moveNode = nodeById.get(moveId);
        const movePosition = positions[moveId];
        if (!moveNode || !movePosition || moveNode.badges?.includes("thesis")) continue;

        const moveRect = rectForNode(moveNode, movePosition);
        const nodeCenterY = (moveRect.top + moveRect.bottom) / 2;
        const labelCenterY = (labelRect.top + labelRect.bottom) / 2;
        const direction = nodeCenterY >= labelCenterY ? 1 : -1;
        const overlapY = Math.min(labelRect.bottom, moveRect.bottom) - Math.max(labelRect.top, moveRect.top);
        const shift = Math.max(28, overlapY + LABEL_GAP + 8) * direction;
        movePosition.y = Math.max(PAD_Y, movePosition.y + shift);
        moved = true;
      }
    }
    if (!moved) break;
  }
}

function relationRank(relation?: RelationType): number {
  switch (relation) {
    case "defines": return 0;
    case "evidences": return 1;
    case "supports": return 2;
    case "qualifies": return 3;
    case "reframes": return 4;
    case "rebuts": return 5;
    case "credibility": return 6;
    case "undercuts": return 7;
    default: return 8;
  }
}

function isCritiqueRelation(relation?: RelationType): boolean {
  return relation === "rebuts" || relation === "undercuts" || relation === "credibility";
}

function isNodeCritiqueRelation(relation?: RelationType): boolean {
  return relation === "rebuts" || relation === "credibility";
}

function nodeRank(node: GraphNode, outgoingRelation?: RelationType): number {
  if (node.type === "evidence" || node.type === "example" || node.type === "measurement" || node.type === "empirical_claim") return 0;
  if (node.type === "hidden_warrant" || node.type === "interpretation") return 1;
  if (node.type === "claim" || node.type === "value_claim") return 2;
  if (node.type === "scope_condition") return 3;
  if (node.type === "bridge") return 4;
  if (node.type === "criterion" || node.type === "terminal_value" || node.type === "definition") return 5;
  if (node.type === "unresolved_crux") return 6;
  if (node.badges?.includes("thesis")) return 9;
  return relationRank(outgoingRelation);
}

export function autoLayout(graph: StrainGraph): Record<ID, { x: number; y: number }> {
  const { nodes, edges } = graph;
  if (nodes.length === 0) return {};
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const outgoingRelation = new Map<ID, RelationType>();
  const critiqueSourceIds = new Set<ID>(edges.filter((edge) => isCritiqueRelation(edge.relation)).map((edge) => edge.source));

  // ---- 1. build adjacency in reading order ----
  // Idea Strains edges point FROM the reason/evidence TO the claim it supports.
  // The canvas now follows that direction visually: foundations on the left,
  // intermediate inferences in the middle, and terminal theses on the right.
  const children = new Map<ID, ID[]>();
  const parents = new Map<ID, ID[]>();
  nodes.forEach((n) => { children.set(n.id, []); parents.set(n.id, []); });

  for (const e of edges) {
    if (!isCritiqueRelation(e.relation) && e.source && e.target) {
      pushUnique(children, e.source, e.target);
      pushUnique(parents, e.target, e.source);
      outgoingRelation.set(e.source, e.relation);
    } else if (e.relation === "undercuts" && e.target_edge) {
      const targetEdge = edges.find((te) => te.id === e.target_edge);
      if (targetEdge?.source && targetEdge.target) {
        // Undercuts attack an inference, so use both endpoints as layout anchors.
        pushUnique(children, targetEdge.source, e.source);
        pushUnique(children, targetEdge.target, e.source);
        pushUnique(parents, e.source, targetEdge.source);
        pushUnique(parents, e.source, targetEdge.target);
        outgoingRelation.set(e.source, e.relation);
      }
    }
  }

  // ---- 2. compute layers via longest path (kahn's with max-depth) ----
  const layer = new Map<ID, number>();
  const inDegree = new Map<ID, number>();
  nodes.forEach((n) => inDegree.set(n.id, parents.get(n.id)!.length));

  // roots = foundations with no incoming non-critique edge.
  const roots = nodes.filter(
    (n) => !n.badges?.includes("thesis") && !critiqueSourceIds.has(n.id) && inDegree.get(n.id) === 0,
  );
  if (roots.length === 0) roots.push(nodes[0]);

  const queue: ID[] = roots.map((r) => r.id);
  roots.forEach((r) => layer.set(r.id, 0));

  while (queue.length) {
    const cur = queue.shift()!;
    const cl = layer.get(cur) ?? 0;
    for (const child of children.get(cur) ?? []) {
      // longest path: take the max
      if (!layer.has(child) || layer.get(child)! < cl + 1) {
        layer.set(child, cl + 1);
      }
      inDegree.set(child, inDegree.get(child)! - 1);
      if (inDegree.get(child) === 0) queue.push(child);
    }
  }

  // handle cycles: unvisited nodes get placed at max layer + 1
  let maxLayer = Math.max(0, ...Array.from(layer.values()));
  nodes.forEach((n) => {
    if (!layer.has(n.id) && !critiqueSourceIds.has(n.id)) {
      layer.set(n.id, maxLayer + 1);
    }
  });

  // handle undercut source nodes: place them just after the edge they undercut.
  for (const e of edges) {
    if (e.relation === "undercuts" && e.target_edge) {
      const targetEdge = edges.find((te) => te.id === e.target_edge);
      if (targetEdge?.source && targetEdge?.target) {
        const maxL = Math.max(
          layer.get(targetEdge.source) ?? 0,
          layer.get(targetEdge.target) ?? 0,
        );
        const cur = layer.get(e.source) ?? 0;
        if (cur <= maxL) layer.set(e.source, maxL + 1);
      }
    }
  }

  // ---- 3. group by layer and sort within layers (barycenter heuristic) ----
  const layers = new Map<number, ID[]>();
  layer.forEach((l, id) => {
    if (critiqueSourceIds.has(id)) return;
    if (!layers.has(l)) layers.set(l, []);
    layers.get(l)!.push(id);
  });

  const positions: Record<ID, { x: number; y: number }> = {};
  const sortedLayerKeys = Array.from(layers.keys()).sort((a, b) => a - b);
  const layerStartColumn = new Map<number, number>();
  let columnCursor = 0;

  for (const l of sortedLayerKeys) {
    const count = layers.get(l)!.length;
    layerStartColumn.set(l, columnCursor);
    columnCursor += Math.max(1, Math.ceil(count / MAX_STACKED_NODES));
  }

  // first pass: assign x and a default y
  for (const l of sortedLayerKeys) {
    for (const id of layers.get(l)!) {
      positions[id] = { x: PAD_X + (layerStartColumn.get(l) ?? 0) * COL_W, y: CENTER_Y };
    }
  }

  // second pass: assign y based on parent barycenter, measured node heights,
  // and bounded subcolumns when a layer would otherwise become too tall.
  for (const l of sortedLayerKeys) {
    const layerNodes = layers.get(l)!;

    // sort by barycenter of parent y-positions (reduces edge crossings)
    layerNodes.sort((a, b) => {
      const pa = parents.get(a) ?? [];
      const pb = parents.get(b) ?? [];
      const ya = pa.length > 0
        ? pa.reduce((s, p) => s + (positions[p]?.y ?? CENTER_Y), 0) / pa.length
        : CENTER_Y;
      const yb = pb.length > 0
        ? pb.reduce((s, p) => s + (positions[p]?.y ?? CENTER_Y), 0) / pb.length
        : CENTER_Y;
      if (Math.abs(ya - yb) > 1) return ya - yb;
      const na = nodeById.get(a);
      const nb = nodeById.get(b);
      const ra = na ? nodeRank(na, outgoingRelation.get(a)) : 99;
      const rb = nb ? nodeRank(nb, outgoingRelation.get(b)) : 99;
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });

    const laneCount = Math.max(1, Math.ceil(layerNodes.length / MAX_STACKED_NODES));
    const lanes: ID[][] = Array.from({ length: laneCount }, () => []);
    const laneHeights = Array.from({ length: laneCount }, () => 0);

    for (const id of layerNodes) {
      const node = nodeById.get(id);
      const h = node ? estimateNodeHeight(node) : 110;
      let bestLane = 0;
      for (let i = 1; i < laneCount; i += 1) {
        if (laneHeights[i] < laneHeights[bestLane]) bestLane = i;
      }
      lanes[bestLane].push(id);
      laneHeights[bestLane] += h + (lanes[bestLane].length > 1 ? ROW_GAP : 0);
    }

    lanes.forEach((lane, laneIndex) => {
      if (lane.length === 0) return;
      const barys = lane.map((id) => {
        const ps = parents.get(id) ?? [];
        return ps.length > 0
          ? ps.reduce((s, p) => s + (positions[p]?.y ?? CENTER_Y), 0) / ps.length
          : CENTER_Y;
      });
      const avgBary = barys.reduce((a, b) => a + b, 0) / barys.length;
      const stagger = laneCount > 1 ? (laneIndex - (laneCount - 1) / 2) * 34 : 0;
      const totalHeight = lane.reduce((sum, id, i) => {
        const node = nodeById.get(id);
        return sum + (node ? estimateNodeHeight(node) : 110) + (i > 0 ? ROW_GAP : 0);
      }, 0);
      let y = Math.max(PAD_Y, avgBary + stagger - totalHeight / 2);
      const x = PAD_X + ((layerStartColumn.get(l) ?? 0) + laneIndex) * COL_W;

      lane.forEach((id) => {
        const node = nodeById.get(id);
        const h = node ? estimateNodeHeight(node) : 110;
        positions[id] = { x, y };
        y += h + ROW_GAP;
      });
    });
  }

  const minY = Math.min(...Object.values(positions).map((p) => p.y));
  if (minY < PAD_Y) {
    const delta = PAD_Y - minY;
    Object.values(positions).forEach((p) => { p.y += delta; });
  }

  const directCritiquesByTarget = new Map<ID, GraphEdge[]>();
  for (const edge of edges) {
    if (!edge.target || !isNodeCritiqueRelation(edge.relation)) continue;
    const list = directCritiquesByTarget.get(edge.target) ?? [];
    list.push(edge);
    directCritiquesByTarget.set(edge.target, list);
  }
  directCritiquesByTarget.forEach((list, targetId) => {
    const target = nodeById.get(targetId);
    const targetPosition = positions[targetId];
    if (!target || !targetPosition) return;
    const targetRect = rectForNode(target, targetPosition);
    const sorted = [...list].sort((a, b) => relationRank(a.relation) - relationRank(b.relation) || a.id.localeCompare(b.id));
    sorted.forEach((edge, index) => {
      const critique = nodeById.get(edge.source);
      if (!critique) return;
      const h = estimateNodeHeight(critique);
      const stagger = (index - (sorted.length - 1) / 2) * (h + CRITIQUE_Y_GAP);
      positions[edge.source] = {
        x: Math.max(PAD_X, targetRect.left - CRITIQUE_X_GAP),
        y: Math.max(PAD_Y, targetRect.bottom + CRITIQUE_Y_GAP + stagger),
      };
    });
  });

  relaxForEdgeLabels(graph, positions, nodeById);

  const relaxedMinY = Math.min(...Object.values(positions).map((p) => p.y));
  if (relaxedMinY < PAD_Y) {
    const delta = PAD_Y - relaxedMinY;
    Object.values(positions).forEach((p) => { p.y += delta; });
  }

  return positions;
}
